import { BittsFilterProp, BittsIcon } from '@crossbeam/bitts';
import { StrictExclude } from '@crossbeam/types';

import { POTENTIAL_REVENUE, TOTAL_OVERLAPS } from '@/constants/amm_grid';
import {
  CUSTOMERS_STANDARD_TYPE,
  OPEN_OPPS_STANDARD_TYPE,
  PROSPECTS_STANDARD_TYPE,
  SegmentType,
} from '@/types/populations';
import type { GeneralAccessLevel } from '@/types/report_team_access';
import {
  ReportColumnType,
  ReportModalState,
  ReportPartnerImpactScore,
  ReportTypeName,
} from '@/types/reports';

import { MDM_PROPERTIES } from './mdm';

// Row limits for free tier account mapping
export const MAX_FREE_ROW_LIMIT = 10;
export const MAX_FAKE_ROW_LIMIT = 25;

export const DEALS_AMOUNT = 'deals_amount';
export const DEALS_COUNT = 'deals_count';
export const OVERLAP_TIME = 'overlap_time';
export const OVERLAPS_WITH = 'overlaps_with';
export const PARTNER_IMPACT = 'partner_impact_scores';
export const POPULATIONS = 'populations';
export const OUR_SEGMENT_FILTERS = 'our_segment_filters';
export const PARTNER_SEGMENT_FILTERS = 'partner_segment_filters';
export const PARTNER_POPULATIONS = 'partner_populations';
export const PARTNERSTACK = 'partnerstack';
export const NOTE = 'note';
// for "is {{population}} of cols"
export const CUSTOMER_OF = 'customer_of';
export const OPPORTUNITY_FOR = 'opportunity_for';
export const PROSPECT_OF = 'prospect_of';
export const CUSTOM_POP_OF = 'custom_populations_of';

/**
 * Partner field `column` names that may be autocompleted for virtual (MOP/ODP)
 * partners.
 */
const VIRTUAL_PARTNER_AUTOCOMPLETE_COLUMN_NAMES = [
  'products',
  'AWS Products',
  'GCP Products',
  'Microsoft Cloud Products',
  'Salesforce Products',
  'SAP Products',
];

export const VIRTUAL_PARTNER_AUTOCOMPLETE_COLUMNS = new Set(
  VIRTUAL_PARTNER_AUTOCOMPLETE_COLUMN_NAMES.map((name) =>
    name.trim().toLowerCase(),
  ),
);

export type SegmentFilterType =
  | typeof CUSTOMER_OF
  | typeof OPPORTUNITY_FOR
  | typeof PROSPECT_OF
  | typeof CUSTOM_POP_OF;

export const SEGMENT_TYPE_MAP = {
  customer_of: 'customers',
  prospect_of: 'prospects',
  opportunity_for: 'open_opportunities',
  custom_populations_of: 'other',
} as const satisfies Record<SegmentFilterType, SegmentType>;

export const SEGMENT_TYPE_TO_COL = {
  customers: 'customer_of',
  prospects: 'prospect_of',
  open_opportunities: 'opportunity_for',
  other: 'custom_populations_of',
} as const satisfies Record<SegmentType, SegmentFilterType>;

export const SEGMENT_FILTERS = [
  'customer_of',
  'opportunity_for',
  'prospect_of',
  'custom_populations_of',
] as const satisfies readonly SegmentFilterType[];

export type CrossbeamFilterType =
  | 'segment_filters'
  | typeof PARTNER_TAGS
  | typeof POPULATIONS;

export const PARTNER_TAGS = 'partner_tags';

export const CROSSBEAM_POP_FILTERS: BittsFilterProp[] = [
  {
    label: 'My Populations',
    value: POPULATIONS,
    faIcon: ['fak', 'population'],
    useSearch: true,
    preserveOrder: true,
    notRemovable: true,
    showOperatorInChip: true,
  },
];

export const CROSSBEAM_SEGMENT_FILTERS: BittsFilterProp[] = [
  {
    label: 'Is a Customer of',
    value: CUSTOMER_OF,
    faIcon: ['fak', 'population'],
    useSearch: true,
    showOperators: false,
    datatype: 'lookup',
  },
  {
    label: 'Is an Open Opportunity of',
    value: OPPORTUNITY_FOR,
    faIcon: ['fak', 'population'],
    useSearch: true,
    showOperators: false,
    datatype: 'lookup',
  },
  {
    label: 'Is a Prospect of',
    value: PROSPECT_OF,
    faIcon: ['fak', 'population'],
    useSearch: true,
    showOperators: false,
    datatype: 'lookup',
  },
  {
    label: 'In Custom Populations of',
    value: CUSTOM_POP_OF,
    faIcon: ['fak', 'population'],
    useSearch: true,
    datatype: 'lookup',
  },
];

export const CROSSBEAM_PARTNER_POP_FILTERS: BittsFilterProp[] = [
  {
    label: 'Partner Populations',
    value: PARTNER_POPULATIONS,
    faIcon: ['fak', 'population'],
    useSearch: true,
    preserveOrder: true,
    notRemovable: true,
    showOperatorInChip: true,
  },
];

export const POPULATION_OPERATOR_OPTIONS = [
  { label: 'is any of', value: 'OR' },
  { label: 'is all of', value: 'AND' },
] as const;

export const PARTNER_TAG_FILTER: BittsFilterProp = {
  label: 'Partner Tag',
  value: PARTNER_TAGS,
  faIcon: ['fak', 'tag'],
  useSearch: true,
};

export const CROSSBEAM_FILTERS = [...CROSSBEAM_POP_FILTERS, PARTNER_TAG_FILTER];

export const STANDARD_TYPE_TO_COL = {
  [CUSTOMERS_STANDARD_TYPE]: CUSTOMER_OF,
  [OPEN_OPPS_STANDARD_TYPE]: OPPORTUNITY_FOR,
  [PROSPECTS_STANDARD_TYPE]: PROSPECT_OF,
};

export const IS_STANDARD_TYPE_OF_COLS = [
  CUSTOMER_OF,
  OPPORTUNITY_FOR,
  PROSPECT_OF,
] as const;
export const IS_POP_OF_COLS = [
  ...IS_STANDARD_TYPE_OF_COLS,
  CUSTOM_POP_OF,
] as const;

export const SOURCE_FIELD_COL = 'source_field';
export const RECORD_NAME_COL = 'record_name';
export const OVERLAPS_WITH_SORT = 'partner_overlap_count';

export const CROSSBEAM_COLUMNS = [
  {
    column_type: 'overlap_time',
    display_name: 'Matched',
    faIcon: ['fak', 'time'],
  },
  {
    column_type: 'customer_of',
    display_name: 'Is a Customer of',
    faIcon: ['fak', 'population'],
  },
  {
    column_type: 'opportunity_for',
    display_name: 'Is an Opportunity for',
    faIcon: ['fak', 'population'],
  },
  {
    column_type: 'prospect_of',
    display_name: 'Is a Prospect of',
    faIcon: ['fak', 'population'],
  },
  {
    column_type: 'custom_populations_of',
    display_name: 'In Custom Populations of',
    faIcon: ['fak', 'population'],
  },
  {
    column_type: 'deals_amount',
    display_name: 'Open Opportunities Amount',
    faIcon: ['fak', 'opportunity'],
  },
  {
    column_type: 'deals_count',
    display_name: 'Open Opportunities Count',
    faIcon: ['fak', 'opportunity'],
  },
  {
    column_type: 'overlaps_with',
    display_name: 'Overlaps With',
    faIcon: ['fak', 'partner'],
  },
  {
    column_type: 'partner_impact_scores',
    display_name: 'Partner Impact',
    faIcon: ['fak', 'partner'],
  },
  {
    column_type: 'populations',
    display_name: 'My Populations',
    faIcon: ['fak', 'population'],
  },
  {
    column_type: 'partner_populations',
    display_name: 'Partner Populations',
    faIcon: ['fak', 'population'],
  },
  {
    column_type: 'partnerstack',
    display_name: 'PartnerStack',
    faIcon: ['fak', 'partner'],
  },
  {
    column_type: 'note',
    display_name: 'Note',
    faIcon: ['fak', 'note'],
  },
] as const satisfies readonly {
  column_type: ReportColumnType;
  display_name: string;
  faIcon: BittsIcon;
}[];

export const FILTER_OPERATORS = ['AND', 'OR'];

export const OWN_TAB = 'own-filters';
export const PARTNER_TAB = 'partner-filters';

export const INITIAL_MODAL_STATE: ReportModalState = {
  modalText: '',
  modalHeader: '',
  filtersToRemove: [],
};

// report cache statuses
export const CACHE_INITIALIZED = 'CACHE_INITIALIZED';
export const PARTIALLY_CACHED = 'PARTIALLY_CACHED';
export const CACHING_FAILED = 'CACHING_FAILED';
export const CACHED = 'CACHED';
export const SKIPPED = 'SKIPPED';

export const REPORT_CACHE_STATUSES = [
  CACHE_INITIALIZED,
  PARTIALLY_CACHED,
  CACHING_FAILED,
  CACHED,
  SKIPPED,
];

// filtering consts
export {
  ARRAY_OPERATORS,
  ARRAY_PAYLOAD_OPERATORS,
  USER_INPUT_OPERATORS,
} from '@/constants/filter-operators';
export const EMPTY_OPERATORS = ['is empty', 'is not empty'];
export const DATETIME = 'datetime';
export const NUMBER = 'number';
export const BOOLEAN = 'boolean';
export const BIGINT = 'bigint';
export const DOUBLE = 'double precision';
export const COMBINATORS = ['AND', 'OR'];

// consolidated report type
export const OVERLAPS = 'overlaps';
export const PIPELINE = 'pipeline';
export const ECOSYSTEM = 'ecosystem';
export const OWN_GREENFIELD = 'own_greenfield';
export const PARTNER_GREENFIELD = 'partner_greenfield';

export const GREENFIELD_CONSOLIDATED_TYPES = [
  OWN_GREENFIELD,
  PARTNER_GREENFIELD,
];

export const OWN_GREENFIELD_CROSSBEAM_COLUMNS = [
  DEALS_AMOUNT,
  DEALS_COUNT,
  POPULATIONS,
  PARTNERSTACK,
];
export const PARTNER_GREENFIELD_CROSSBEAM_COLUMNS = [
  PARTNER_POPULATIONS,
  ...IS_POP_OF_COLS,
];

export const SINGLE_OVERLAPS_TYPE = 'single-partner-overlaps';
export const GREENFIELD_TYPE = 'single-partner-greenfield';
export const POTENTIAL_REVENUE_TYPE = 'single-partner-oppty';
export const ALL_PARTNERS_TYPE = 'standard';
export const CUSTOM_TYPE = 'custom';
export const PARTNER_TAGS_TYPE = 'partner-tags';

export const REPORT_TYPE_NAMES = {
  SINGLE_OVERLAPS_TYPE,
  GREENFIELD_TYPE,
  POTENTIAL_REVENUE_TYPE,
  ALL_PARTNERS_TYPE,
  CUSTOM_TYPE,
  PARTNER_TAGS_TYPE,
} as const satisfies Record<string, ReportTypeName>;

export const TYPES = [
  ALL_PARTNERS_TYPE,
  CUSTOM_TYPE,
  GREENFIELD_TYPE,
  PARTNER_TAGS_TYPE,
  POTENTIAL_REVENUE_TYPE,
  SINGLE_OVERLAPS_TYPE,
];

export const CROSSBEAM_SOURCE_ID = 'crossbeam';

export const OWN_DEFAULT_SEGMENTS = [
  'customers',
  'open_opportunities',
  'other',
] as const;

export const PARTNER_DEFAULT_SEGMENTS = [
  'customers',
  'open_opportunities',
  'other',
] as const;

const CUSTOMER_OF_FIELD = {
  label: 'Is a Customer of',
  display_name: 'Is a Customer of',
  id: CUSTOMER_OF,
};
const OPPORTUNITY_FOR_FIELD = {
  label: 'Is an Opportunity for',
  display_name: 'Is an Opportunity for',
  id: OPPORTUNITY_FOR,
};
const PROSPECT_OF_FIELD = {
  label: 'Is a Prospect of',
  display_name: 'Is a Prospect of',
  id: PROSPECT_OF,
};
const CUSTOM_POP_OF_FIELD = {
  label: 'In Custom Populations of',
  display_name: 'In Custom Populations of',
  id: CUSTOM_POP_OF,
};

export const SEGMENT_TYPE_TO_FIELD: Record<
  SegmentType,
  typeof CUSTOMER_OF_FIELD
> = {
  customers: CUSTOMER_OF_FIELD,
  open_opportunities: OPPORTUNITY_FOR_FIELD,
  prospects: PROSPECT_OF_FIELD,
  other: CUSTOM_POP_OF_FIELD,
};

export const SEGMENT_FILTERS_FIELDS = [
  CUSTOMER_OF_FIELD,
  OPPORTUNITY_FOR_FIELD,
  PROSPECT_OF_FIELD,
  CUSTOM_POP_OF_FIELD,
];

export const CROSSBEAM_SOURCE = {
  id: CROSSBEAM_SOURCE_ID,
  table: 'Crossbeam',
  fields: [
    ...SEGMENT_FILTERS_FIELDS,
    {
      label: 'My Populations',
      display_name: 'My Populations',
      id: POPULATIONS,
    },
    {
      label: 'Populations',
      display_name: 'Populations',
      id: OUR_SEGMENT_FILTERS,
    },
    {
      label: 'Partner Populations',
      display_name: 'Partner Populations',
      id: PARTNER_SEGMENT_FILTERS,
    },
    {
      label: 'Partner Tags',
      display_name: 'Partner Tags',
      id: PARTNER_TAGS,
    },
  ],
};

export const OWN_ACCOUNT_EXEC = 'own-account-exec';
export const PARTNER_ACCOUNT_EXEC = 'partner-account-exec';

export const PARTNER_SOURCE_FIELD_COLS = [
  MDM_PROPERTIES.account.NAME,
  MDM_PROPERTIES.account.WEBSITE,
];

const DEFAULT_COLS = [
  NOTE,
  POPULATIONS,
  OWN_ACCOUNT_EXEC,
  PARTNER_ACCOUNT_EXEC,
  MDM_PROPERTIES.account.BILLING_COUNTRY,
  MDM_PROPERTIES.account.INDUSTRY,
  MDM_PROPERTIES.account.EMPLOYEES,
  DEALS_AMOUNT,
  DEALS_COUNT,
  OVERLAP_TIME,
  PARTNERSTACK,
  PARTNER_POPULATIONS,
] as const;

export type DefaultColumnType =
  | (typeof DEFAULT_COLS)[number]
  | (typeof IS_POP_OF_COLS)[number]
  | typeof PARTNER_IMPACT
  | typeof OVERLAPS_WITH;
const DEFAULT_COLS_WITH_IS_POP_OF_COLS: DefaultColumnType[] = (
  [...DEFAULT_COLS] as DefaultColumnType[]
)
  .toSpliced(1, 0, OVERLAPS_WITH)
  .toSpliced(3, 0, ...IS_POP_OF_COLS);
const DEFAULT_COLS_WITH_STANDARD_TYPE: DefaultColumnType[] =
  DEFAULT_COLS_WITH_IS_POP_OF_COLS.filter(
    (col) => col !== 'custom_populations_of',
  );

export type CompatibleDefaultColumnType =
  | StrictExclude<ReportTypeName, 'single-partner-greenfield'>
  | 'single-partner-greenfield__own_greenfield'
  | 'single-partner-greenfield__partner_greenfield';
export const COMPATIBLE_DEFAULT_COLS = {
  [SINGLE_OVERLAPS_TYPE]: [...DEFAULT_COLS],
  [POTENTIAL_REVENUE_TYPE]: [...DEFAULT_COLS],
  [ALL_PARTNERS_TYPE]: [
    ...DEFAULT_COLS_WITH_STANDARD_TYPE.toSpliced(
      2,
      0,
      'partner_impact_scores',
    ).filter((col) => col !== 'partner-account-exec'),
  ],
  [PARTNER_TAGS_TYPE]: DEFAULT_COLS_WITH_STANDARD_TYPE,
  [CUSTOM_TYPE]: DEFAULT_COLS_WITH_IS_POP_OF_COLS,
  [`${GREENFIELD_TYPE}__${OWN_GREENFIELD}`]: [
    NOTE,
    OWN_ACCOUNT_EXEC,
    MDM_PROPERTIES.account.BILLING_COUNTRY,
    MDM_PROPERTIES.account.INDUSTRY,
    MDM_PROPERTIES.account.EMPLOYEES,
    POPULATIONS,
    PARTNERSTACK,
  ],
  [`${GREENFIELD_TYPE}__${PARTNER_GREENFIELD}`]: [
    ...IS_POP_OF_COLS,
    NOTE,
    PARTNER_ACCOUNT_EXEC,
    PARTNER_POPULATIONS,
  ],
} as const satisfies Record<CompatibleDefaultColumnType, DefaultColumnType[]>;

// report types
export const REPORT_TYPES = [
  {
    backgroundColor: 'bg-info-background-weak',
    description:
      'Get actionable insights about the state of individual partnerships',
    displayName: 'Single Partner',
    multiPartner: false,
    icon: ['fak', 'artificial-intelligence'],
    iconColor: 'text-warning-accent',
    type: SINGLE_OVERLAPS_TYPE,
    viewName: TOTAL_OVERLAPS,
  },
  {
    backgroundColor: 'bg-upsell-background-weak',
    description:
      'Leverage Potential Revenue to uncover the highest ROI opportunities',
    displayName: 'Pipeline',
    multiPartner: false,
    icon: ['fak', 'revenue'],
    iconColor: 'text-upsell-accent',
    requiresUpgrade: true,
    type: POTENTIAL_REVENUE_TYPE,
    viewName: POTENTIAL_REVENUE,
  },
  {
    backgroundColor: 'bg-success-background-weak',
    description: 'Discover all non-overlapping accounts with partners',
    displayName: 'Greenfield',
    multiPartner: false,
    icon: ['fak', 'greenfield'],
    iconColor: 'text-success-accent',
    requiresUpgrade: true,
    type: GREENFIELD_TYPE,
  },
  {
    backgroundColor: 'bg-info-background-weak',
    description: 'Track and measure insights by partner tag categories',
    displayName: 'Partner Tag',
    multiPartner: true,
    icon: ['fak', 'tag'],
    iconColor: 'text-info-accent',
    requiresUpgrade: true,
    tagText: 'Multi-Partner',
    type: PARTNER_TAGS_TYPE,
  },
  {
    backgroundColor: 'bg-info-background-weak',
    description:
      'See a 360-degree ecosystem landscape with an all-partners view',
    displayName: 'Ecosystem',
    multiPartner: true,
    icon: ['fak', 'team'],
    iconColor: 'text-info-accent',
    requiresUpgrade: true,
    tagText: 'All Partner',
    type: ALL_PARTNERS_TYPE,
  },
  {
    backgroundColor: 'bg-info-background-weak',
    description: 'Slice and dice your data with a custom build',
    displayName: 'Custom',
    multiPartner: true,
    icon: ['fak', 'wrench'],
    iconColor: 'text-neutral-accent',
    requiresUpgrade: true,
    tagText: 'Multi-Partner',
    type: CUSTOM_TYPE,
  },
];

export const MULTI_PARTNER_REPORT_TYPES = [
  ALL_PARTNERS_TYPE,
  CUSTOM_TYPE,
  PARTNER_TAGS_TYPE,
];
export const MULTI_POPULATION_REPORT_TYPES = [
  ...MULTI_PARTNER_REPORT_TYPES,
  GREENFIELD_TYPE,
];

export const FREE_CROSSBEAM_COLUMNS = [
  OVERLAP_TIME,
  OVERLAPS_WITH,
  POPULATIONS,
  PARTNER_POPULATIONS,
  NOTE,
  ...IS_POP_OF_COLS,
];

// metrics consts
export const TOP_OPEN_OPPORTUNITIES = 'top_open_opportunities';
export const TOP_CUSTOMERS = 'top_customers';
export const TOP_DEAL_SIZE_MULTIPLIER = 'top_deal_size_multiplier';
export const TOP_WIN_RATE_MULTIPLIER = 'top_win_rate_multiplier';
export const TOP_TIME_TO_CLOSE_MULTIPLIER = 'top_time_to_close_multiplier';

export const PERCENT_MULTIPLIERS = [
  TOP_DEAL_SIZE_MULTIPLIER,
  TOP_WIN_RATE_MULTIPLIER,
  TOP_TIME_TO_CLOSE_MULTIPLIER,
];
export const METRIC_ORDER = [
  TOP_OPEN_OPPORTUNITIES,
  TOP_CUSTOMERS,
  TOP_WIN_RATE_MULTIPLIER,
  TOP_DEAL_SIZE_MULTIPLIER,
  TOP_TIME_TO_CLOSE_MULTIPLIER,
];

// partner impact
export const EXPORT_IMPACT_TO_NAME_LOOKUP = {
  high: '(High Impact)',
  medium: '(Medium Impact)',
  low: '(Low Impact)',
  none: '(No Impact)',
  unknown: null,
} as const satisfies Record<ReportPartnerImpactScore, string | null>;

// error states
export const INVALID_OWN_FILTERS = 'own_filters_changed';
export const INVALID_PARTNER_FILTERS = 'partner_filters_changed';
export const NO_OWN_POPULATIONS = 'no_own_populations';
export const NO_PARTNER_POPULATIONS = 'no_partner_populations';
const BOTH_FILTERS_INVALID = 'both-filters-invalid';

export const REPORT_ERROR_STATES = {
  INVALID_OWN_FILTERS,
  INVALID_PARTNER_FILTERS,
  NO_OWN_POPULATIONS,
  NO_PARTNER_POPULATIONS,
  BOTH_FILTERS_INVALID,
} as const;
export type ReportErrorState =
  (typeof REPORT_ERROR_STATES)[keyof typeof REPORT_ERROR_STATES];

export const GENERAL_ACCESS_ONLY_INVITED =
  'only-invited' satisfies GeneralAccessLevel;
